import type { RequestStatus } from '@features/request/requestSlice';
import { useRequest } from '@features/request/useRequest';
import type { CatalogLimitedCostTypeExtendedData, ProjectMethodologyEnum } from '@gql/graphql';

import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { getLimitedCostTypesGql } from '../CatalogLimitedTypeQueries';

type TProps = {
    methodology: ProjectMethodologyEnum;
};

interface ReturnValue {
    limitedTypesData: Omit<CatalogLimitedCostTypeExtendedData, '__typename'>[];
    limitedTypesStatus: RequestStatus;
    updateLimitedTypesData: () => void;
}

export const useGetLimitedTypesData = ({ methodology }: TProps): ReturnValue => {
    const dispatch = useDispatch();

    const variables = useMemo(() => ({ methodology }), [methodology]);

    const [data, limitedTypesStatus, , request] = useRequest({
        requestId: 'limitedCostTypes',
        query: getLimitedCostTypesGql,
        variables,
    });

    const updateLimitedTypesData = useCallback(() => {
        dispatch(request(variables));
    }, [dispatch, request, variables]);

    const sortedTypesData = useMemo(
        () =>
            [...(data?.limitedCostTypes ?? [])].sort((a, b) => {
                if (a.isSystem === b.isSystem) {
                    return a.name.localeCompare(b.name);
                }

                return a.isSystem ? 1 : -1;
            }),
        [data],
    );

    return useMemo(
        () => ({
            limitedTypesData: sortedTypesData,
            updateLimitedTypesData,
            limitedTypesStatus,
        }),
        [sortedTypesData, updateLimitedTypesData, limitedTypesStatus],
    );
};
